/*  iceberg-ui  */
import { WithContext } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, Button, Typography } from '@mui/material';

/* Shared */
import {
    AppNameLinkCell,
    ApplicationBusinessOwnerCell,
    ClassificationCell,
    DateFormatNoTimeStringCell,
    LicenseDataCell,
    LifecycleCell,
    ManagementStateCell,
    PublisherCell,
    RiskCell,
    SSOSupportIconsCell,
    SelectAllHeaderCell,
    SelectRowCell,
    StringCell,
    TagLinksCell,
    TenantAppSourcesCell,
    UsageChartCell,
    UserBubblesCell,
    WorkLoginPercentCell,
    applicationBusinessOwnerFilter,
    applicationBusinessOwnerFilterFn,
    classificationCellFilter,
    classificationCellFilterFn,
    dateFilter,
    lastXDays,
    licenseDataCellFilter,
    licenseDataCellFilterFn,
    lifecycleCellFilter,
    managementStageFilter,
    managementStateFilterFn,
    riskCellFilter,
    ssoSupportFilter,
    ssoSupportFilterFn,
    tenantAppSourcesFilterFn
} from 'application/Shared/Components/Cells';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import {
    Header,
    ManagedTableV2Context,
    ManagedTableV2ContextProvider,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { sortByLicenseStatus } from 'application/Shared/Components/Table/tableSortingFunctions';
import {
    filterFns,
    tenantAppSourcesFilter
} from 'application/Shared/Components/TableColumnFilters';
import {
    sortByRiskFn,
    sortByTimestamp,
    sortByUserCount
} from 'application/Shared/Components/TableSortFunctions';
import { TABLENAMES } from 'application/Shared/Constants';
import { Notify, SSOUtils } from 'application/Shared/Helpers';
import { BulkClassifyModal } from 'application/Shared/Modals';
import { Confirm } from 'application/Shared/Modals/Confirm';
import { debug } from 'application/Shared/Utils';

/* Yarn */
import { isEqual } from 'lodash';

/*  Objects  */
import NotesModal from 'objects/Application/NotesModal';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WorkatoConnectionsModal } from '../../Workato/WorkatoConnectionsModal';
import { getWorkatoStatus } from '../../Workato/functions';
import { WorkatoStatus } from '../../Workato/types';
import {
    ClientAppsTableContext,
    ClientAppsTableContextProvider,
    useClientAppsTableContext
} from './ClientAppsTableContext';
import { AppInfoCards } from './internal/AppInfoCards';
import { ClientAppsTable } from './internal/ClientAppsTable';

const BulkClassify = ({ setBulkClassify }) => {
    const { t } = useTranslation();
    const { table } = useManagedTableV2Context();

    return (
        <Button
            color="primary"
            data-testid="bulkClassifyBtn"
            data-trackid="classifyApp"
            disabled={!table.getSelectedRowModel().rows.length}
            onClick={() => {
                setBulkClassify(true);
            }}
            size="anmSmall"
            variant="outlined"
        >
            {t('Common.classify')}
        </Button>
    );
};

const BulkClassifyModalAction = () => {
    const {
        bulkClassify,
        classifyId,
        defaultCategoryId,
        setBulkClassify,
        setClassifyId
    } = useClientAppsTableContext();

    const { setRerender, table } = useManagedTableV2Context();

    const filteredApps = [];

    if (classifyId) {
        const app = table
            .getRowModel()
            .rows.filter((row) => row.original.id === classifyId);
        filteredApps.push(app[0].original);
    } else {
        table.getSelectedRowModel().rows.forEach((row) => {
            filteredApps.push(row.original);
        });
    }

    return (
        <BulkClassifyModal
            open={bulkClassify}
            applications={filteredApps}
            closeModal={() => {
                setBulkClassify(false);
                setClassifyId(null);
                table.resetRowSelection();
            }}
            onSave={(saved) => {
                setBulkClassify(false);
                setClassifyId(null);
                table.resetRowSelection();
                if (saved) {
                    setRerender(true);
                }
            }}
            defaultCategoryId={defaultCategoryId}
        />
    );
};

const handlePreprocessRows = (rows) => {
    if (!rows?.length) {
        return rows;
    }
    rows.forEach((row) => {
        // mutate the row to include SSO Support information for filtering
        if (!row.ssoSupport) {
            row.ssoSupport = SSOUtils.tenantAppGetSSOSupport(row); // eslint-disable-line no-param-reassign
        }
    });

    // Filter classified and removed apps.
    // Removed apps, but unclassified, stay, that is the "old" behaviour.
    // Probably because when one removes an app, classified gets set as true.
    // It is not clear to me why we do that, though.
    const returnedRows = rows.filter((row) => !(row.classified && row.removed));

    return returnedRows;
};

const handleRows = ({
    rowMap: currentRowMap,
    rows,
    setRowMap,
    t,
    userNameMap
}) => {
    if (!rows?.length) {
        return;
    }

    const updatedRowMap = {};
    rows.forEach((row) => {
        const {
            azureSignInCount,
            businessOwnerPlatformUserId,
            cloudActivityCount,
            cloudLoginCount,
            desktopActivityCount,
            desktopData,
            downloadActivityCount,
            extensionData,
            googleSignInCount,
            googleSSO,
            id,
            managed3rdParty,
            managedByPartner,
            microsoftSSO,
            noBusinessOwnerRequired,
            oktaSignInCount,
            quickScanCount
        } = row;

        let type = '';
        if (desktopData && extensionData) {
            type = 'Hybrid';
        } else if (desktopData) {
            type = 'Desktop';
        } else if (extensionData) {
            type = 'Extension';
        }

        const sourceNames = [];
        if (azureSignInCount) {
            sourceNames.push(t('Common.microsoftEntraID.loginData'));
        }
        if (googleSignInCount) {
            sourceNames.push(t('Common.googleLoginData'));
        }
        if (oktaSignInCount) {
            sourceNames.push(t('Common.oktaLoginData'));
        }
        if (downloadActivityCount) {
            sourceNames.push(t('Common.downloadActivityData'));
        }
        if (desktopActivityCount) {
            sourceNames.push(t('Common.desktopData'));
        }
        if (cloudLoginCount) {
            sourceNames.push(t('Common.cloudLoginData'));
        }
        if (quickScanCount) {
            sourceNames.push(t('Common.quickScanLoginFound'));
        }
        if (cloudActivityCount) {
            sourceNames.push(t('Common.extensionData'));
        }
        if (googleSSO) {
            sourceNames.push(t('Tooltips.Badge.google_sso'));
        }
        if (microsoftSSO) {
            sourceNames.push(t('Tooltips.Badge.microsoft_sso'));
        }

        let ownerName = t('Common.not_assigned');

        if (noBusinessOwnerRequired) {
            ownerName = t('Common.not_required');
        }

        if (managedByPartner) {
            ownerName = t('Common.managed_by_partner');
        }

        if (managed3rdParty) {
            ownerName = t('Common.managed_by_3rd_party');
        }

        if (userNameMap[businessOwnerPlatformUserId]) {
            ownerName = userNameMap[businessOwnerPlatformUserId];
        }

        updatedRowMap[id] = {
            ...row,
            ownerName,
            sourceNames: sourceNames.join(', '),
            type
        };
    });

    if (!isEqual(updatedRowMap, currentRowMap)) {
        setRowMap(updatedRowMap);
    }
};

const deleteApp = async ({
    clientId,
    id,
    putTenantApplicationLink,
    setRerender,
    t
}) => {
    const params = { classified: true, removed: true };
    if (clientId) {
        params.platformTenantId = clientId;
    }

    try {
        await putTenantApplicationLink({ id, params });
        setRerender(true);
    } catch (e) {
        debug.e('Delete Error', e);
        Notify.e(t('Application.delete.fail'));
    }
};

const Actions = ({
    clientId,
    putTenantApplicationLink,
    row,
    setBulkClassify,
    setClassifyId,
    setRerender,
    t,
    table
}) => {
    const [promptDelete, setPromptDelete] = useState(false);
    const [openSaasOpsConfigurationModal, setOpenSaasOpsConfigurationModal] =
        useState(false);

    const { workatoConnection, name: tenantAppName } = row.original;
    const { workatoProvider, name: originalAppName } = row.original.application;

    const name = tenantAppName || originalAppName;

    const isSaasOpsEnabled = !!workatoProvider;

    const workatoStatus = getWorkatoStatus(workatoConnection);

    const saasOpsConfigurationRequired =
        workatoStatus !== WorkatoStatus.connected;

    const options = [
        {
            isParent: true,
            key: 'appManagement.title',
            label: t('Common.appManagement'),
            value: 'appManagement'
        },
        {
            dataTrackid: `table_${TABLENAMES.CLIENTAPPS}_actions_classify`,
            key: 'classify',
            label: t('Common.classify'),
            value: 'classify'
        },
        {
            dataTrackid: `table_${TABLENAMES.CLIENTAPPS}_actions_delete`,
            key: 'delete',
            label: t('Common.delete'),
            value: 'delete'
        }
    ];

    if (isSaasOpsEnabled) {
        options.push(
            ...[
                {
                    isParent: true,
                    key: 'SaasOps.title',
                    label: t('SaasOps.title'),
                    value: 'saasOps'
                },
                {
                    dataTrackid: `table_${TABLENAMES.CLIENTAPPS}_actions_configureSaasOps`,
                    disabled: !saasOpsConfigurationRequired,
                    key: 'Common.configure',
                    label: t('Common.configure'),
                    value: 'saasOpsConfigure'
                }
            ]
        );
    }

    return (
        <Box display="flex" alignItems="center" justifyContent="flex-end">
            <KebabMenu
                options={options}
                onClick={(value) => {
                    if (value === 'classify') {
                        setClassifyId(row.original.id);
                        setBulkClassify(true);
                    }
                    if (value === 'delete') {
                        setPromptDelete(true);
                    }
                    if (value === 'saasOpsConfigure') {
                        setOpenSaasOpsConfigurationModal(true);
                    }
                }}
                data-testid="unclassifiedAppsTableActions"
            />
            {promptDelete && (
                <DeleteModal
                    promptDelete={promptDelete}
                    clientId={clientId}
                    row={row}
                    putTenantApplicationLink={putTenantApplicationLink}
                    setRerender={setRerender}
                    t={t}
                    table={table}
                    setPromptDelete={setPromptDelete}
                />
            )}
            {openSaasOpsConfigurationModal && (
                <WorkatoConnectionsModal
                    clientId={clientId}
                    workatoProvider={workatoProvider}
                    displayName={name}
                    setOpenModal={setOpenSaasOpsConfigurationModal}
                    onSuccessFn={() => {
                        setOpenSaasOpsConfigurationModal(false);
                        setTimeout(() => {
                            setRerender(true);
                        }, 2000);
                        Notify.s(
                            t('SaasOps.integration.connection.success', {
                                appName: name
                            })
                        );
                    }}
                />
            )}
        </Box>
    );
};

const DeleteModal = ({
    promptDelete,
    clientId,
    row,
    putTenantApplicationLink,
    setRerender,
    t,
    table,
    setPromptDelete
}) => (
    <Confirm
        modalOpen={!!promptDelete}
        promptConfirmFn={() => {
            deleteApp({
                clientId,
                id: row.original.id,
                putTenantApplicationLink,
                setRerender,
                t
            });
            table.resetRowSelection();
        }}
        promptCancelFn={() => {
            setPromptDelete(false);
            table.resetRowSelection();
        }}
        promptTitle={t('ManageApplication.confirmToDeleteApp')}
        promptText={
            <Box display="flex" flexDirection="column">
                <Typography
                    variant="p"
                    sx={{
                        fontWeight: 600,
                        mb: '16px'
                    }}
                >
                    {t('ManageApplication.areYouSureDeleteApp', {
                        appName: row.original.application?.name
                    })}
                </Typography>
                <Typography variant="p">
                    {t('ApplicationRemove.description')}
                </Typography>
            </Box>
        }
        textConfirmButton={t('Common.yes')}
    />
);

const ActionsCell = ({ row }) => (
    <WithContext context={ClientAppsTableContext}>
        {({
            clientId,
            putTenantApplicationLink,
            setBulkClassify,
            setClassifyId,
            t
        }) => (
            <WithContext context={ManagedTableV2Context}>
                {({ setRerender, table }) => (
                    <Actions
                        clientId={clientId}
                        putTenantApplicationLink={putTenantApplicationLink}
                        row={row}
                        setBulkClassify={setBulkClassify}
                        setClassifyId={setClassifyId}
                        setRerender={setRerender}
                        t={t}
                        table={table}
                    />
                )}
            </WithContext>
        )}
    </WithContext>
);

const getShowBrowserHistoryBadge = (row) => {
    // If quickScanCount = num > 0, then cloudActivityCount will be at least that num
    // Therefore, I am not including it in the othersSum
    const othersSum =
        row.azureSignInCount +
        row.googleSignInCount +
        row.downloadActivityCount +
        row.desktopActivityCount +
        row.cloudLoginCount +
        row.googleSSO +
        row.microsoftSSO;
    if (
        row.quickScanCount > 0 &&
        othersSum === 0 &&
        row.quickScanCount >= row.cloudActivityCount
    ) {
        return true;
    }
    return false;
};

const NotesCell = (props) => {
    const { row } = props;
    const appId = row.original.application.id;

    return (
        <WithContext context={ClientAppsTableContext}>
            {({
                clientNotes,
                communityNotes,
                partner,
                partnerNotes,
                reloadNotes
            }) => (
                <NotesModal
                    application={row.original.application}
                    clientNotes={clientNotes[appId] ?? []}
                    communityNotes={communityNotes[appId] ?? []}
                    data-testid="renderNotesNotesCell"
                    onSave={(reload) => {
                        if (reload) {
                            reloadNotes();
                        }
                    }}
                    partner={partner}
                    partnerNotes={partnerNotes[appId] ?? []}
                />
            )}
        </WithContext>
    );
};

const columns = ({ t }) => [
    {
        cell: SelectRowCell,
        enableHiding: false,
        enableSorting: false,
        header: SelectAllHeaderCell,
        id: 'selectAll',
        maxSize: 50,
        meta: {
            align: 'center'
        },
        size: 50,
        testId: 'selectAll'
    },
    {
        accessorKey: 'application.name',
        cell: (props) => (
            <AppNameLinkCell
                fromPage={t('Common.applications')}
                showBrowserHistoryBadge={getShowBrowserHistoryBadge(
                    props.row.original
                )}
                withCategory={false}
                {...props}
            />
        ),
        enableColumnFilter: false,
        enableHiding: false,
        header: Header,
        id: 'application',
        name: 'Common.application',
        size: 275,
        testId: 'application'
    },
    {
        accessorKey: 'application.category',
        cell: StringCell,
        filterFn: filterFns.theRealArrIncludesSome,
        header: Header,
        id: 'category',
        name: 'Common.category',
        size: 100,
        testId: 'application.category'
    },
    {
        accessorKey: 'application.subCategory',
        cell: StringCell,
        filterFn: filterFns.arrIncludesSomeWithNulls,
        header: Header,
        id: 'subCategory',
        meta: { filter: { defaultOptions: [], includeNullValue: true } },
        name: 'Common.subCategory',
        size: 100,
        testId: 'application.subCategory'
    },
    {
        cell: NotesCell,
        enableColumnFilter: false,
        enableSorting: false,
        header: Header,
        id: 'notes',
        maxSize: 50,
        name: 'Common.notes',
        size: 50,
        testId: 'notes'
    },
    {
        accessorKey: 'dateAdded',
        cell: DateFormatNoTimeStringCell,
        filterFn: lastXDays,
        header: Header,
        id: 'dateAdded',
        meta: { filter: dateFilter },
        name: 'Common.dateDiscovered',
        size: 150,
        sortingFn: sortByTimestamp,
        testId: 'dateDiscovered'
    },
    {
        accessorKey: 'rankedUserInfo',
        cell: (props) => <UserBubblesCell {...props} dataIsUser />,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'users',
        name: 'Common.active_users',
        size: 150,
        sortingFn: sortByUserCount,
        testId: 'activeUsers'
    },
    {
        accessorKey: 'ssoSupport',
        cell: SSOSupportIconsCell,
        enableGlobalFilter: false,
        filterFn: ssoSupportFilterFn,
        header: Header,
        id: 'ssoCoverage',
        meta: { filter: ssoSupportFilter },
        name: 'Common.ssoSupport',
        size: 150,
        testId: 'ssoCoverage'
    },
    {
        accessorKey: 'licenseData',
        cell: LicenseDataCell,
        enableGlobalFilter: false,
        filterFn: licenseDataCellFilterFn,
        header: Header,
        id: 'licenseData',
        meta: { filter: licenseDataCellFilter },
        name: 'Common.licenseData',
        size: 150,
        sortingFn: sortByLicenseStatus,
        testId: 'licenseData'
    },
    {
        accessorKey: 'sources',
        cell: TenantAppSourcesCell,
        enableGlobalFilter: false,
        filterFn: tenantAppSourcesFilterFn,
        header: Header,
        id: 'source',
        meta: { filter: tenantAppSourcesFilter },
        name: 'Common.sources',
        size: 150,
        testId: 'sources'
    },
    {
        accessorKey: 'application.risk',
        cell: RiskCell,
        filterFn: filterFns.theRealArrIncludesSome,
        header: Header,
        id: 'riskFilter',
        meta: { filter: riskCellFilter },
        name: 'Common.risk',
        size: 150,
        sortingFn: sortByRiskFn,
        testId: 'application.risk'
    },
    {
        accessorKey: 'classified',
        cell: ClassificationCell,
        filterFn: classificationCellFilterFn,
        header: Header,
        id: 'classified',
        meta: { filter: classificationCellFilter },
        name: 'Common.classification',
        size: 175,
        testId: 'classification'
    },
    {
        accessorKey: 'status',
        cell: LifecycleCell,
        filterFn: filterFns.theRealArrIncludesSome,
        header: Header,
        id: 'status',
        meta: { filter: lifecycleCellFilter },
        name: 'Common.lifecycleStage',
        size: 175,
        testId: 'lifecycleStage'
    },
    {
        accessorKey: 'tagLinks',
        cell: TagLinksCell,
        enableColumnFilter: true,
        enableSorting: false,
        filterFn: filterFns.arrIncludesSomeTags,
        getUniqueValues: (row) =>
            row.tagLinks?.map((t) => t.globalTag.name) || [],
        header: Header,
        id: 'tags',
        meta: { filter: { defaultOptions: [], includeNullValue: true } },
        name: 'Common.tags',
        size: 175,
        testId: 'tags'
    },
    {
        accessorKey: 'managementStage',
        cell: ManagementStateCell,
        filterFn: managementStateFilterFn,
        header: Header,
        id: 'managementStage',
        meta: { filter: managementStageFilter },
        name: 'Common.managed',
        size: 150,
        testId: 'managed'
    },
    {
        accessorKey: 'businessOwner',
        cell: ApplicationBusinessOwnerCell,
        enableSorting: false,
        filterFn: applicationBusinessOwnerFilterFn,
        header: Header,
        id: 'businessOwnerType',
        meta: { filter: applicationBusinessOwnerFilter },
        name: 'Common.business_owner',
        size: 175,
        testId: 'businessOwner'
    },
    {
        accessorKey: 'usage',
        cell: UsageChartCell,
        enableColumnFilter: false,
        header: Header,
        id: 'usage',
        name: 'Common.usage_chart',
        size: 150,
        testId: 'usageChart'
    },
    {
        accessorKey: 'workLoginPercent',
        cell: WorkLoginPercentCell,
        enableColumnFilter: false,
        header: Header,
        id: 'workLoginPercent',
        name: 'Common.work_usage',
        size: 150,
        testId: 'workUsage'
    },
    {
        accessorKey: 'lastUsed',
        cell: DateFormatNoTimeStringCell,
        filterFn: lastXDays,
        header: Header,
        id: 'lastUsed',
        meta: { filter: dateFilter },
        name: 'Common.last_used',
        size: 150,
        sortingFn: sortByTimestamp,
        testId: 'lastUsed'
    },
    {
        accessorKey: 'application.publisherName',
        cell: PublisherCell,
        filterFn: filterFns.theRealArrIncludesSome,
        header: Header,
        id: 'publisher',
        name: 'Common.publisher',
        size: 100,
        testId: 'application.publisherName'
    },
    {
        accessorKey: 'actions',
        cell: ActionsCell,
        enableColumnFilter: false,
        enableHiding: false,
        enableSorting: false,
        header: Header,
        id: 'actions',
        maxSize: 10,
        meta: { align: 'right', header: { justifyContent: 'flex-end' } },
        name: 'Common.actions',
        size: 10,
        testId: 'actions'
    }
];

const baseParameters = {
    excludeDisabled: true,
    includeApplication: true,
    includeManagementStage: true,
    includePublisher: true,
    includeRankedUsers: 20, // Limit user bubbles to our "power" users
    includeTagLinks: true,
    includeTenantLicenseData: true,
    includeUncataloged: true,
    includeUncategorizedWorkLogins: true, // Returns removed apps
    includeWorkato: true,
    size: -1
};

const toggles = (t) => [
    {
        // We only want to exclude personal apps in the unclassified view.
        // If a personal app has been classified, then we should show it on the classified page.
        defaultEnabled: true,
        key: 'excludePersonal',
        parameter: { field: 'excludePersonal', value: true },
        toggleText: `${t('Common.exclude_personal_applications')}`
    },
    {
        defaultEnabled: false,
        key: 'excludeCloud',
        parameter: { field: 'excludeCloud', value: true },
        toggleText: `${t('Common.exclude_cloud_applications')}`
    },
    {
        defaultEnabled: false,
        key: 'excludeDesktop',
        parameter: { field: 'excludeDesktop', value: true },
        toggleText: `${t('Common.exclude_desktop_applications')}`
    },
    {
        defaultEnabled: false,
        key: 'includeAllUncategorized',
        parameter: {
            field: 'includeAllUncategorized',
            value: true
        },
        toggleText: `${t('Common.unanalyzedApplications')}`,
        tooltip: 'unanalyzedApplications'
    }
];

const ClientAppsPage = () => {
    const { t } = useTranslation();

    const [resetRowSelection, setResetRowSelection] = useState(false);
    const {
        bulkClassify,
        columnPinning,
        columnVisibility,
        customTableViews,
        loadingCustomTableViews,
        refetchCustomTableViews,
        rowMap,
        setBulkClassify,
        setRowMap,
        tableName,
        tableViews,
        userNameMap
    } = useClientAppsTableContext();

    // We need the customTableViews loaded, they can be empty.
    return (
        !loadingCustomTableViews && (
            <ManagedTableV2ContextProvider
                baseParameters={baseParameters}
                bulkActions={<BulkClassify setBulkClassify={setBulkClassify} />}
                columns={columns({ t })}
                customTableViews={customTableViews}
                getRowId="id"
                handleRows={(rows) =>
                    handleRows({
                        rowMap,
                        rows,
                        setRowMap,
                        t,
                        userNameMap
                    })
                }
                initialState={{
                    columnPinning,
                    columnVisibility
                }}
                mode="client"
                preprocessRows={handlePreprocessRows}
                refetchCustomTableViews={refetchCustomTableViews}
                resetRowSelection={resetRowSelection}
                rowsApi="/tenantApp"
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                setResetRowSelection={setResetRowSelection}
                sortDirection="DESC"
                sortKey="dateAdded"
                tableName={tableName}
                tableViews={tableViews}
                toggles={toggles(t)}
            >
                <AppInfoCards />
                <ClientAppsTable />
                {bulkClassify && <BulkClassifyModalAction />}
            </ManagedTableV2ContextProvider>
        )
    );
};

export const ClientAppsDashboard = () => (
    <Box data-testid="clientAppsDashboard">
        <ClientAppsTableContextProvider>
            <ClientAppsPage />
        </ClientAppsTableContextProvider>
    </Box>
);
